// src/components/WelcomeScreen.tsx - Fixed version with robust animations

import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Globe2 } from "lucide-react";
import LanguageModal from "./languageModel";
import { Theme } from "../types/theme";
import chatbotImg from "../assets/Priya-.png";


interface WelcomeScreenProps {
  theme: Theme;
  agentName?: string;
  onStart: (userName: string) => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ theme, agentName, onStart }) => {
  const [userName, setUserName] = useState("");
  const [showLangPopup, setShowLangPopup] = useState(false);
  const [language, setLanguage] = useState("en");
  const [animate, setAnimate] = useState(false);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationInjected = useRef(false);

  // Inject animations once when component mounts
  useEffect(() => {
    if (!animationInjected.current) {
      injectAnimationStyles();
      animationInjected.current = true;
    }

    // Set mounted after a brief delay to ensure smooth transition
    const timer = setTimeout(() => {
      setMounted(true);
      setAnimate(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const injectAnimationStyles = () => {
    const existingStyle = document.getElementById('welcome-screen-animations');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.id = 'welcome-screen-animations';
    style.textContent = `
      @keyframes smart-agent-float {
        0%, 100% { 
          transform: translateY(0px) rotate(0deg) scale(1); 
          opacity: 0.3;
        }
        33% { 
          transform: translateY(-15px) rotate(2deg) scale(1.05); 
          opacity: 0.25;
        }
        66% { 
          transform: translateY(8px) rotate(-2deg) scale(0.95); 
          opacity: 0.35;
        }
      }

      @keyframes smart-agent-float-reverse {
        0%, 100% { 
          transform: translateY(0px) rotate(0deg) scale(1); 
          opacity: 0.2;
        }
        33% { 
          transform: translateY(12px) rotate(-1.5deg) scale(1.08); 
          opacity: 0.3;
        }
        66% { 
          transform: translateY(-8px) rotate(1.5deg) scale(0.92); 
          opacity: 0.15;
        }
      }

      @keyframes smart-agent-fade-in-up {
        0% {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
          pointer-events: none;
        }
        100% {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }
      }

      @keyframes smart-agent-fade-in-scale {
        0% {
          opacity: 0;
          transform: scale(0.8) translateY(20px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      @keyframes smart-agent-glow-pulse {
        0%, 100% {
          filter: brightness(1) saturate(1);
        }
        50% {
          filter: brightness(1.1) saturate(1.2);
        }
      }

      .smart-agent-float-orb-1 {
        animation: smart-agent-float 6s ease-in-out infinite;
      }

      .smart-agent-float-orb-2 {
        animation: smart-agent-float-reverse 8s ease-in-out infinite;
      }

      .smart-agent-animate-in {
        animation: smart-agent-fade-in-up 0.8s ease-out forwards;
      }

      .smart-agent-animate-in-delay-1 {
        animation: smart-agent-fade-in-up 0.8s ease-out 0.2s forwards;
        opacity: 0;
      }

      .smart-agent-animate-in-delay-2 {
        animation: smart-agent-fade-in-up 0.8s ease-out 0.4s forwards;
        opacity: 0;
      }

      .smart-agent-animate-in-delay-3 {
        animation: smart-agent-fade-in-up 0.8s ease-out 0.6s forwards;
        opacity: 0;
      }

      .smart-agent-avatar-glow {
        animation: smart-agent-glow-pulse 3s ease-in-out infinite;
      }

      .smart-agent-hover-scale {
        transition: transform 0.2s ease-out, box-shadow 0.2s ease-out !important;
      }

      .smart-agent-hover-scale:hover {
        transform: scale(1.02) !important;
      }

      .smart-agent-button-hover {
        transition: all 0.2s ease-out !important;
      }

      .smart-agent-button-hover:hover:not(:disabled) {
        transform: scale(1.05) !important;
        box-shadow: 0 8px 25px rgba(0,0,0,0.3) !important;
      }

      .smart-agent-button-hover:active:not(:disabled) {
        transform: scale(0.95) !important;
      }

      /* Ensure animations work in shadow DOM */
      :host .smart-agent-float-orb-1,
      .smart-agent-widget .smart-agent-float-orb-1 {
        animation: smart-agent-float 6s ease-in-out infinite;
      }

      :host .smart-agent-float-orb-2,
      .smart-agent-widget .smart-agent-float-orb-2 {
        animation: smart-agent-float-reverse 8s ease-in-out infinite;
      }
    `;

    document.head.appendChild(style);
  };

  const handleStart = () => {
    if (userName.trim()) onStart(userName.trim());
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Generate dynamic gradient based on primary color and theme mode
  const generateDynamicGradient = () => {
    const primaryColor = theme.primaryColor || "#3b82f6";

    if (theme.mode === "dark") {
      return `linear-gradient(135deg, 
        ${hexToRgba(primaryColor, 0.2)} 0%, 
        rgba(15, 15, 15, 0.95) 25%,
        rgba(30, 30, 30, 0.9) 50%,
        ${hexToRgba(primaryColor, 0.1)} 100%
      )`;
    } else {
      return `linear-gradient(135deg, 
        rgba(255, 255, 255, 0.9) 0%,
        ${hexToRgba(primaryColor, 0.1)} 20%,
        ${hexToRgba(primaryColor, 0.2)} 50%,
        rgba(255, 255, 255, 0.8) 80%,
        ${hexToRgba(primaryColor, 0.15)} 100%
      )`;
    }
  };

  // Glass effect styles for the card
  const getGlassCardStyles = () => {
    const primaryColor = theme.primaryColor || "#3b82f6";

    if (theme.mode === "dark") {
      return {
        background: `linear-gradient(135deg, 
          ${hexToRgba(primaryColor, 0.15)} 0%,
          rgba(30, 30, 30, 0.4) 30%,
          rgba(20, 20, 20, 0.6) 100%
        )`,
        backdropFilter: "blur(20px)",
        border: `1px solid ${hexToRgba(primaryColor, 0.3)}`,
        boxShadow: `
          0 8px 32px ${hexToRgba(primaryColor, 0.2)},
          inset 0 1px 0 ${hexToRgba(primaryColor, 0.2)},
          0 0 0 1px rgba(255, 255, 255, 0.05)
        `
      };
    } else {
      return {
        background: `linear-gradient(135deg, 
          rgba(255, 255, 255, 0.7) 0%,
          ${hexToRgba(primaryColor, 0.05)} 30%,
          rgba(255, 255, 255, 0.4) 100%
        )`,
        backdropFilter: "blur(20px)",
        border: `1px solid ${hexToRgba(primaryColor, 0.2)}`,
        boxShadow: `
          0 8px 32px ${hexToRgba(primaryColor, 0.15)},
          inset 0 1px 0 rgba(255, 255, 255, 0.3),
          0 0 0 1px ${hexToRgba(primaryColor, 0.1)}
        `
      };
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex flex-col items-center justify-center px-4 relative overflow-hidden"
      style={{
        background: generateDynamicGradient(),
        opacity: mounted ? 1 : 0,
        transition: 'opacity 0.3s ease-out',
      }}
    >
      {/* Animated background elements */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ pointerEvents: "none", zIndex: 0 }}
      >
        {/* Floating orbs with improved animations */}
        <div
          className={`absolute w-64 h-64 rounded-full blur-3xl smart-agent-float-orb-1 ${animate ? 'opacity-30' : 'opacity-0'
            }`}
          style={{
            background: `radial-gradient(circle, ${theme.primaryColor || "#3b82f6"} 0%, transparent 70%)`,
            top: "10%",
            left: "20%",
            transition: 'opacity 1s ease-in-out'
          }}
        />
        <div
          className={`absolute w-48 h-48 rounded-full blur-2xl smart-agent-float-orb-2 ${animate ? 'opacity-25' : 'opacity-0'
            }`}
          style={{
            background: `radial-gradient(circle, ${theme.primaryColor || "#3b82f6"} 0%, transparent 70%)`,
            bottom: "15%",
            right: "25%",
            transition: 'opacity 1.5s ease-in-out'
          }}
        />
      </div>

      {/* Avatar Section with improved animation */}
      <div
        className={`flex justify-end relative z-10 ${animate ? 'smart-agent-animate-in' : ''
          }`}

      >
        <img src={chatbotImg}
          alt="AI Assistant"
          className={`w-24 sm:w-32 md:w-40 object-contain smart-agent-avatar-glow ${animate ? '' : 'opacity-0'
            }`}
          style={{
            pointerEvents: "none",
            marginLeft: "215px",
            filter: `drop-shadow(0 10px 20px ${hexToRgba(theme.primaryColor || "#3b82f6", 0.3)})`,
            transition: 'opacity 0.8s ease-out'
          }}
        />
      </div>

      {/* Main Glass Card with staggered animations */}
      <div
        className={`rounded-2xl px-4 sm:px-6 md:px-8 lg:px-10 pt-4 sm:pt-5 pb-5 sm:pb-6 
                    w-full max-w-[90%] sm:max-w-sm md:max-w-md lg:max-w-lg 
                    mx-auto relative smart-agent-animate-in-delay-1 mb-2 ${animate ? 'show' : ''
          }`}
        style={{
          ...getGlassCardStyles(),
          zIndex: 10
        }}
      >
        {/* Greeting with animation */}
        <div
          className={`text-center mb-6 smart-agent-animate-in-delay-2 ${animate ? 'show' : ''
            }`}
        >
          <h1
            className="text-3xl font-semibold tracking-tight mb-1"
            style={{
              color: theme.mode === "dark" ? "#ffffff" : "#1f2937",
              textShadow: theme.mode === "dark"
                ? `0 0 12px ${hexToRgba(theme.primaryColor || "#3b82f6", 0.25)}`
                : "none",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500
            }}
          >
            Meet {agentName || "Ben"}
          </h1>

          <p
            className="text-base leading-relaxed"
            style={{
              color: theme.mode === "dark" ? "#d1d5db" : "#6b7280",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300
            }}
          >
            Your Personal Smart Agent
          </p>
        </div>

        {/* Input Section with animation */}
        <div
          className={`smart-agent-animate-in-delay-3 ${animate ? 'show' : ''
            }`}
        >
          <div
            className="flex items-center mb-6 rounded-xl px-3 gap-2 flex-nowrap"
            style={{
              background: theme.mode === "dark"
                ? `linear-gradient(135deg, 
                    rgba(255, 255, 255, 0.1) 0%, 
                    rgba(255, 255, 255, 0.05) 100%
                  )`
                : `linear-gradient(135deg, 
                    rgba(255, 255, 255, 0.8) 0%, 
                    rgba(255, 255, 255, 0.4) 100%
                  )`,
              border: `1px solid ${hexToRgba(theme.primaryColor || "#3b82f6", 0.2)}`,
              backdropFilter: "blur(10px)"
            }}
          >
            {/* Input */}
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleStart()}
              placeholder="Enter your name..."
              className="flex-1 min-w-0 bg-transparent text-base h-12 px-2"
              style={{
                outline: "none",
                boxShadow: "none",
                fontFamily: "'Inter', sans-serif",
                fontSize: "15px",
                color: theme.mode === "dark" ? "#ffffff" : "#1f2937"
              }}
            />

            {/* Button */}
            <button
              onClick={handleStart}
              disabled={!userName.trim()}
              className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg 
                         disabled:opacity-40 disabled:cursor-not-allowed
                         focus:outline-none transition-all duration-200
                         hover:scale-105 active:scale-95"
              style={{
                background: `linear-gradient(135deg, 
                  ${theme.primaryColor || "#3b82f6"} 0%, 
                  ${hexToRgba(theme.primaryColor || "#3b82f6", 0.8)} 100%
                )`,
                color: "#ffffff",
                boxShadow: `0 4px 15px ${hexToRgba(theme.primaryColor || "#3b82f6", 0.4)}`
              }}
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Language Selector */}
          {/* <button
            onClick={() => setShowLangPopup(true)}
            className="w-full flex items-center justify-center gap-2 
                       text-sm font-medium transition-all duration-200 py-2
                       hover:scale-105 mb-5"
            style={{
              color: theme.primaryColor || "#3b82f6",
              textShadow: theme.mode === "dark"
                ? `0 0 10px ${hexToRgba(theme.primaryColor || "#3b82f6", 0.5)}`
                : "none"
            }}
          >
            <Globe2 className="w-4 h-4" />
            Choose Language
          </button> */}
        </div>

      </div>

      {/* Tagline under the card */}
      {/* <p
        className={`text-center text-sm mt-2 smart-agent-animate-in-delay-3 ${animate ? 'show' : ''}`}
        style={{
          color: theme.mode === "dark" ? "#9ca3af" : "#6b7280",
          fontFamily: "'Inter', sans-serif",
          fontSize: "11px",
          
         
        }}
      >
       {agentName}  chats are recorded for quality.
      </p> */}
      <div className="mt-3 flex items-center justify-center gap-2">
        <p
          className="text-xs text-center flex items-center gap-1.5"
          style={{ color: theme.textSecondaryColor }}
        >
          <span className="opacity-70">Powered by</span>
          <span className="font-semibold" style={{ color: theme.primaryColor }}>
            Deemsys
          </span>
          <span className="opacity-70">•</span>
          <span className="opacity-70">Private & secure</span>
        </p>
      </div>

      {/* Language Modal */}
      <LanguageModal
        isOpen={showLangPopup}
        onClose={() => setShowLangPopup(false)}
        onSave={(lang) => setLanguage(lang)}
        initialLang={language}
      />
    </div>
  );
};

export default WelcomeScreen;